/**
 * AssetDetailModal — Sprint 18.5 reusable deep-dive modal.
 * Opens on AssetCard click. Fetches /api/crypto/asset/:symbol (or passed data).
 *
 * Props:
 *   symbol (required)
 *   asset_type: 'crypto' | 'stock'
 *   on_close: callback
 *   on_action: callback after actions that need parent refresh
 */

import { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, ExternalLink, Edit2, Save } from 'lucide-react';
import ConvictionSparkline from './ConvictionSparkline.jsx';

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };

function fmtPrice(p, assetType) {
  if (p == null) return '—';
  if (assetType === 'crypto' && p < 1) return `$${p.toFixed(p < 0.01 ? 6 : 4)}`;
  if (p < 100) return `$${p.toFixed(2)}`;
  return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtPct(x) {
  if (x == null) return '—';
  return `${x >= 0 ? '+' : ''}${x.toFixed(2)}%`;
}

export default function AssetDetailModal({ symbol, asset_type = 'crypto', on_close, on_action }) {
  const [data, setData] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [editingPosition, setEditingPosition] = useState(false);
  const [posTier, setPosTier] = useState('monitor');
  const [posHoldingPct, setPosHoldingPct] = useState('');
  const [posEntryPrice, setPosEntryPrice] = useState('');
  const [posNotes, setPosNotes] = useState('');

  useEffect(() => {
    async function load() {
      const endpoint = asset_type === 'crypto' ? `/api/crypto/asset/${symbol}` : `/api/watchlist/ticker/${symbol}`;
      try {
        const r = await fetch(endpoint);
        if (r.ok) {
          const d = await r.json();
          setData(d);
          setTargetInput(d.asset?.dca_target_price || '');
          setNoteInput(d.asset?.dca_target_note || '');
          setPosTier(d.asset?.tier || 'monitor');
          setPosHoldingPct(d.asset?.holding_pct ?? '');
          setPosEntryPrice(d.asset?.entry_price ?? '');
          setPosNotes(d.asset?.personal_notes || '');
        }
      } catch {}
    }
    load();
  }, [symbol, asset_type]);

  // Close on Escape key
  useEffect(() => {
    function onEsc(e) { if (e.key === 'Escape') on_close?.(); }
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [on_close]);

  async function saveTarget() {
    await fetch(`/api/crypto/assets/${symbol}/dca-target`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_price: targetInput ? parseFloat(targetInput) : null, note: noteInput || null }),
    });
    setEditingTarget(false);
    on_action?.();
    const r = await fetch(`/api/crypto/asset/${symbol}`); if (r.ok) setData(await r.json());
  }

  async function removeAsset() {
    if (!confirm(`Remove ${symbol} from watchlist? (soft-delete)`)) return;
    const endpoint = asset_type === 'crypto' ? `/api/crypto/assets/${symbol}` : `/api/watchlist/${symbol}`;
    await fetch(endpoint, { method: 'DELETE' });
    on_action?.();
    on_close?.();
  }

  async function savePosition() {
    await fetch(`/api/watchlist/${symbol}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tier: posTier,
        holding_pct: posHoldingPct === '' ? null : parseFloat(posHoldingPct),
        entry_price: posEntryPrice === '' ? null : parseFloat(posEntryPrice),
        personal_notes: posNotes || null,
      }),
    });
    setEditingPosition(false);
    on_action?.();
    const r = await fetch(`/api/watchlist/ticker/${symbol}`); if (r.ok) setData(await r.json());
  }

  async function bumpTier(direction) {
    const endpoint = `/api/watchlist/${symbol}/${direction > 0 ? 'promote' : 'demote'}`;
    await fetch(endpoint, { method: 'POST' });
    on_action?.();
    const r = await fetch(`/api/watchlist/ticker/${symbol}`); if (r.ok) setData(await r.json());
  }

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-korinth-surface border border-korinth-border rounded-lg p-6 text-xs font-mono text-korinth-muted">Loading {symbol}…</div>
      </div>
    );
  }

  const { asset, price, kairos, themes, catalysts, shadow_pnl, polymarket = [] } = data;
  const changeColor = (price?.change_24h || 0) >= 0 ? '#10b981' : '#ef4444';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={on_close}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-korinth-surface border border-korinth-border rounded-lg max-w-3xl w-full my-8 shadow-2xl"
      >
        {/* Hero */}
        <div className="p-4 border-b border-korinth-border flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-mono font-bold text-white">{asset.symbol}</span>
              <span className="text-sm text-korinth-muted">{asset.name}</span>
              {asset.category && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-korinth-border text-korinth-muted">{asset.category}</span>}
              {asset.tier && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent">{asset.tier.replace(/_/g, ' ')}</span>}
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-3xl font-mono font-bold text-white">{fmtPrice(price?.current, asset_type)}</span>
              <div className="flex flex-col text-[10px] font-mono">
                <span style={{ color: (price?.change_1h || 0) >= 0 ? '#10b981' : '#ef4444' }}>1h {fmtPct(price?.change_1h)}</span>
                <span style={{ color: changeColor }}>24h {fmtPct(price?.change_24h)}</span>
                <span style={{ color: (price?.change_7d || 0) >= 0 ? '#10b981' : '#ef4444' }}>7d {fmtPct(price?.change_7d)}</span>
              </div>
              <ConvictionSparkline data={price?.sparkline_7d} stance="neutral" width={140} height={36} />
            </div>
            <div className="flex gap-4 mt-2 text-[10px] font-mono text-korinth-muted">
              {price?.volume_24h && <span>Vol: ${(price.volume_24h / 1e9).toFixed(2)}B</span>}
              {price?.market_cap && <span>MCap: ${(price.market_cap / 1e9).toFixed(2)}B</span>}
              {asset.coingecko_id && (
                <a href={`https://www.coingecko.com/en/coins/${asset.coingecko_id}`} target="_blank" rel="noopener noreferrer" className="text-korinth-accent hover:underline inline-flex items-center gap-0.5">
                  CoinGecko <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
          <button onClick={on_close} className="text-korinth-muted hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* Your Position — Stock flavor (Sprint 19: tier, holding%, entry, notes) */}
          {asset_type !== 'crypto' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">Your Position</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => bumpTier(-1)} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg text-korinth-muted hover:text-white border border-korinth-border">↓ demote</button>
                  <button onClick={() => bumpTier(+1)} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg text-korinth-muted hover:text-white border border-korinth-border">↑ promote</button>
                </div>
              </div>
              {editingPosition ? (
                <div className="space-y-2 bg-korinth-bg rounded p-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[9px] text-korinth-muted">Tier</label>
                      <select value={posTier} onChange={e => setPosTier(e.target.value)}
                        className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white">
                        <option value="core">Core</option>
                        <option value="active">Active</option>
                        <option value="monitor">Monitor</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] text-korinth-muted">Holding %</label>
                      <input type="number" step="any" value={posHoldingPct} onChange={e => setPosHoldingPct(e.target.value)}
                        className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white" />
                    </div>
                    <div>
                      <label className="text-[9px] text-korinth-muted">Entry price</label>
                      <input type="number" step="any" value={posEntryPrice} onChange={e => setPosEntryPrice(e.target.value)}
                        className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] text-korinth-muted">Notes</label>
                    <input type="text" value={posNotes} onChange={e => setPosNotes(e.target.value)}
                      className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={savePosition} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30">
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button onClick={() => setEditingPosition(false)} className="text-[10px] text-korinth-muted hover:text-korinth-text">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-korinth-bg rounded p-3 text-xs font-mono flex-wrap">
                  <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent">{asset.tier || 'monitor'}</span>
                  {asset.holding_pct != null && <span className="text-korinth-text">Holding <span className="text-white font-bold">{asset.holding_pct}%</span></span>}
                  {asset.entry_price != null && <span className="text-korinth-text">Entry <span className="text-white font-bold">${asset.entry_price}</span></span>}
                  {asset.personal_notes && <span className="text-korinth-muted italic">— {asset.personal_notes}</span>}
                  {!asset.holding_pct && !asset.entry_price && !asset.personal_notes && <span className="text-korinth-muted italic">No position data</span>}
                  <button onClick={() => setEditingPosition(true)} className="ml-auto text-korinth-muted hover:text-korinth-accent"><Edit2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          )}

          {/* Your Position — Crypto DCA */}
          {asset_type === 'crypto' && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Your Position (DCA)</div>
              {editingTarget ? (
                <div className="space-y-2 bg-korinth-bg rounded p-3">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-[9px] text-korinth-muted">Target price</label>
                      <input type="number" step="any" value={targetInput} onChange={e => setTargetInput(e.target.value)}
                        className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-xs font-mono text-white" />
                    </div>
                    <div className="flex-[2]">
                      <label className="text-[9px] text-korinth-muted">Note</label>
                      <input type="text" value={noteInput} onChange={e => setNoteInput(e.target.value)}
                        className="block w-full bg-korinth-surface border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-white" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveTarget} className="flex items-center gap-1 text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30">
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button onClick={() => setEditingTarget(false)} className="text-[10px] text-korinth-muted hover:text-korinth-text">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-korinth-bg rounded p-3 text-xs font-mono">
                  {asset.holding_pct != null && (
                    <>
                      <span className="text-korinth-muted">Holding:</span>
                      <span className="text-white font-bold">{asset.holding_pct}%</span>
                      <span className="text-korinth-muted">·</span>
                    </>
                  )}
                  {asset.dca_target_price ? (
                    <>
                      <span className="text-korinth-muted">Target:</span>
                      <span className="text-white font-bold">{fmtPrice(asset.dca_target_price, asset_type)}</span>
                      {asset.dca_target_note && <span className="text-korinth-text italic">— {asset.dca_target_note}</span>}
                    </>
                  ) : <span className="text-korinth-muted italic">No target set</span>}
                  <button onClick={() => setEditingTarget(true)} className="ml-auto text-korinth-muted hover:text-korinth-accent"><Edit2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          )}

          {/* KAIROS section */}
          <div>
            <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">KAIROS View</div>
            {kairos?.all_recommendations?.length > 0 && (
              <div className="space-y-1 mb-2">
                {kairos.all_recommendations.map(r => (
                  <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-xs font-mono">
                    <span className={r.direction === 'buy' ? 'text-korinth-accent font-bold' : r.direction === 'sell' ? 'text-korinth-danger font-bold' : 'text-korinth-warning font-bold'}>
                      {r.direction?.toUpperCase()}
                    </span>
                    <span className="text-korinth-accent">{r.conviction_overall?.toFixed(1)}/10</span>
                    <span className="text-[9px] text-korinth-muted">T{r.conviction_tactical || '—'} · S{r.conviction_swing || '—'} · St{r.conviction_strategic || '—'}</span>
                    {r.position_size_recommended_pct && <span className="text-[9px] text-korinth-muted">size {r.position_size_recommended_pct}%</span>}
                  </div>
                ))}
              </div>
            )}
            {kairos?.predictions?.length > 0 && (
              <div className="space-y-0.5 mb-2">
                {kairos.predictions.map(p => (
                  <div key={p.id} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg/50 rounded text-[10px] font-mono">
                    <span className={p.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>
                      {p.direction === 'bullish' ? '▲' : '▼'}
                    </span>
                    <span className="text-white">{p.direction} {p.timeframe}</span>
                    <span className="text-korinth-text">@ {p.confidence}%</span>
                  </div>
                ))}
              </div>
            )}
            {kairos?.hit_rate_30d != null && (
              <div className="text-[10px] font-mono text-korinth-muted">
                30d hit rate on {asset.symbol}: <span className="text-korinth-accent font-bold">{kairos.hit_rate_30d}%</span> ({kairos.closed_trades_count} closed trades)
              </div>
            )}
            {(!kairos?.all_recommendations?.length && !kairos?.predictions?.length) && (
              <div className="text-[10px] font-mono text-korinth-muted italic">No active KAIROS view on {asset.symbol}</div>
            )}
          </div>

          {/* Themes */}
          {themes?.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Themes ({themes.length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {themes.map(t => {
                  const color = STANCE_COLORS[t.stance] || '#6b7280';
                  const arrow = t.stance === 'bullish' ? '▲' : t.stance === 'bearish' ? '▼' : '→';
                  return (
                    <a key={t.id} href="/themes" className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono hover:border-korinth-accent/30 border border-transparent">
                      <span className="text-white font-bold">{t.name}</span>
                      <span style={{ color }}>{arrow} {(t.conviction || 0).toFixed(1)}</span>
                      <span className="text-[8px] text-korinth-muted ml-auto">{t.link_direction === 'benefits_from' ? 'benefits' : 'hurt by'} · ×{t.link_weight}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Catalysts */}
          {catalysts?.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Recent Catalysts ({catalysts.length})</div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {catalysts.map(c => {
                  const credPct = c.source_credibility != null ? Math.round(c.source_credibility * 100) : null;
                  const credColor = credPct == null ? '#6b7280' : credPct >= 60 ? '#10b981' : credPct >= 40 ? '#f59e0b' : '#ef4444';
                  return (
                    <div key={c.id} className="flex items-start gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                      <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-korinth-border text-korinth-muted flex-shrink-0">{c.catalyst_type}</span>
                      <span className="text-korinth-text flex-1">{c.headline}</span>
                      {credPct != null && <span className="text-[8px] font-bold px-1 rounded flex-shrink-0" style={{ color: credColor, backgroundColor: credColor + '15' }}>{credPct}%</span>}
                      <span className="text-[8px] text-korinth-muted/60 flex-shrink-0">{c.observed_at?.slice(5, 16)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Polymarket (Sprint 19) */}
          {polymarket?.length > 0 && (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Polymarket Odds ({polymarket.length})</div>
              <div className="space-y-1">
                {polymarket.map(pm => {
                  const divergent = (pm.kairos_divergence || 0) > 0.15;
                  return (
                    <div key={pm.id} className="flex items-start gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                      <span className="text-korinth-text flex-1">{pm.question}</span>
                      <span className="text-white font-bold">{((pm.yes_price || 0) * 100).toFixed(0)}%</span>
                      {divergent && <span className="text-[8px] font-bold px-1 rounded bg-korinth-warning/15 text-korinth-warning">Δ {((pm.kairos_divergence || 0) * 100).toFixed(0)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Shadow P&L */}
          {shadow_pnl?.trades_count > 0 ? (
            <div>
              <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1.5">Shadow P&L</div>
              <div className="flex gap-4 mb-2 text-xs font-mono">
                <span>Total: <span className={shadow_pnl.total_usd >= 0 ? 'text-korinth-accent font-bold' : 'text-korinth-danger font-bold'}>${shadow_pnl.total_usd.toLocaleString()}</span></span>
                <span className="text-korinth-muted">{shadow_pnl.wins_count}/{shadow_pnl.trades_count} wins</span>
              </div>
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {shadow_pnl.last_10_trades.map(t => (
                  <div key={t.id} className="flex items-center gap-2 px-2 py-1 bg-korinth-bg rounded text-[9px] font-mono">
                    <span className={t.direction === 'long' ? 'text-korinth-accent' : 'text-korinth-danger'}>{t.direction?.toUpperCase()}</span>
                    <span className="text-korinth-muted">{fmtPrice(t.entry_price, asset_type)} → {fmtPrice(t.exit_price, asset_type)}</span>
                    <span className={(t.realized_pnl_pct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{fmtPct(t.realized_pnl_pct)}</span>
                    <span className="text-[8px] text-korinth-muted ml-auto">{t.exit_time?.slice(5, 16)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-[10px] font-mono text-korinth-muted italic">No closed shadow trades yet on {asset.symbol}</div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-korinth-border/20">
            <button onClick={removeAsset} className="text-[10px] font-mono px-3 py-1 rounded bg-korinth-danger/10 text-korinth-danger border border-korinth-danger/30 hover:bg-korinth-danger/20">
              Remove from list
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
